import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { getTotpStatus } from "@/services/totpService";
import { practisesAnyProfession } from "@/lib/is-professional";

const SETUP_PATH = "/security/two-factor";

/**
 * Whether the app behind the gate may run.
 *
 * `holding` is the important one and it is NOT the same as `blocking`. The
 * status call is a round trip, and a page mounted while it is in flight
 * fires its own queries, every one of which the server refuses — which is
 * how a professional ended up reading "No se pudieron cargar las
 * solicitudes." behind the very modal that explains why. So the layout holds
 * page content from the first paint, not from the moment the answer lands.
 */
export function useTwoFactorGate(): { blocking: boolean; holding: boolean } {
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Any profession, not the two that existed when this gate was written: a
  // third one answered NO here and skipped the second factor entirely.
  const isProfessional = practisesAnyProfession(user);

  const { data: status, isPending } = useQuery({
    queryKey: ["me", "totp", "status"],
    queryFn: getTotpStatus,
    enabled: isProfessional,
    staleTime: 30_000,
  });

  // The page at SETUP_PATH renders the same flow unwrapped, and it must keep
  // working: it is the one route an un-enrolled professional has to be able
  // to use.
  const onSetupRoute = pathname.startsWith(SETUP_PATH);
  const blocking =
    isProfessional === true && status?.enrollmentRequired === true && !onSetupRoute;

  return {
    blocking,
    holding: blocking || (isProfessional === true && isPending && !onSetupRoute),
  };
}

